import styled from '@emotion/styled';
import { Bar1D, BarSingle1D, Model, Pie1D, Subtitle } from '@gameonly/core';
import { useTranslation } from 'react-i18next';
import { GREEN, MEDIUM_BLUE, PINK, PURPLE, YELLOW } from '../colors';
import { Bottom, Title } from '../layout';
import { PdfLegendDispatch, PdfResultDispatch } from '../PdfResultsDispatch';
import {
  DetailedPageNumberType,
  getSubtitleResult,
  PaginatedResult,
} from '../utils';
import { Footer } from './Footer';
import { PageLayout } from './ResultFullPage';

export const compactFontSize = '0.7rem';

const Main = styled.div`
  background-color: transparent;
  flex: 1;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;
`;

const ResultItem = styled.div<{ backgroundColor?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ backgroundColor }) =>
    backgroundColor || 'transparent'};
  gap: 4px;
  border-radius: 8px;
  padding: 8px;
`;

const GraphTitle = styled.p<{ color?: string }>`
  padding: 6px 12px;
  font-size: 16px;
  line-height: 16px;
  align-self: center;
  background-color: ${({ color }) => color || 'black'};
  color: white;
  font-weight: 700;
`;

const Graph = styled.div`
  width: 100%;
  height: 50%;
`;

const Description = styled.p`
  font-size: ${compactFontSize};
`;

const Legend = styled.div`
  width: 100%;
  flex: 1;
`;

const GRAPH_CONFIG: Record<number, { backgroundColor: string; color: string }> =
  {
    0: { backgroundColor: '#e9eff5', color: GREEN },
    1: { backgroundColor: 'transparent', color: MEDIUM_BLUE },
    2: { backgroundColor: '#e1e7f1', color: PURPLE },
    3: { backgroundColor: 'transparent', color: PINK },
    4: { backgroundColor: '#e1e7f1', color: YELLOW },
    5: { backgroundColor: '#e9eff5', color: GREEN },
  };

type GraphType = Pie1D | Bar1D | BarSingle1D;

const ResultsDetailedPage = ({
  paginatedResult,
  model,
  year,
}: {
  paginatedResult: PaginatedResult;
  model?: Model;
  year: number | undefined;
}) => {
  const { t } = useTranslation();
  if (!model) return null;
  const subtitleResult = getSubtitleResult(paginatedResult.results);
  const subtitlePrefix = getSubtitleTitleTranslationPrefix(
    subtitleResult,
    model.name,
  );
  const title = getResultsDetailedPageTitle(
    t(subtitlePrefix),
    paginatedResult.detailedPageNumber,
  );
  const graphResults = paginatedResult.results?.filter(
    (result) =>
      result.type === 'barSingle1D' ||
      result.type === 'pie1D' ||
      result.type === 'bar1D',
  ) as GraphType[];
  if (graphResults.length === 0) return null;
  return (
    <PageLayout backgroundColor="white" withBackgroundImage backgroundVariant='topRight'>
      <Title>{title?.toLocaleUpperCase()}</Title>
      <Main>
        {graphResults?.slice(0, 5).map((result, index) => {
          const config = GRAPH_CONFIG[index] || {};
          const translationPrefix = `model:${model?.name.toLowerCase()}.result.${result.code?.toLowerCase()}`;
          const description = t(`${translationPrefix}.description`);
          return (
            <ResultItem key={index} backgroundColor={config.backgroundColor}>
              <GraphTitle color={config.color}>
                {t(`${translationPrefix}.title`).toLocaleUpperCase()}
              </GraphTitle>
              <Graph>
                <PdfResultDispatch
                  result={result}
                  model={model}
                  compact={true}
                />
              </Graph>
              {description && <Description>{description}</Description>}
              <Legend>
                <PdfLegendDispatch
                  result={result}
                  model={model}
                  compact={true}
                  color={config.color}
                />
              </Legend>
            </ResultItem>
          );
        })}
      </Main>
      <Bottom>
        <Footer
          year={year}
          pageNumber={paginatedResult.pageNumber}
          backgroundType="light"
        />
      </Bottom>
    </PageLayout>
  );
};

export const getSubtitleTitleTranslationPrefix = (
  subtitleResult: Subtitle | null,
  modelName: string,
) => {
  return `model:${modelName.toLowerCase()}.result.${subtitleResult?.code?.toLowerCase()}.text`;
};

const getResultsDetailedPageTitle = (
  subtitleText: string,
  detailedPageNumber?: DetailedPageNumberType | undefined,
): string | null => {
  return `${subtitleText}${
    detailedPageNumber
      ? ` (${detailedPageNumber.currentPage}/${detailedPageNumber.totalPage})`
      : ''
  }`;
};

export default ResultsDetailedPage;
